import { Close, Delete, Edit, History as HistoryIcon, ReceiptLong } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Breadcrumbs, Button, CircularProgress, Dialog, DialogContent, Grid, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { Suspense, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import AddCustomerModalV4 from '../../../common/AddCustomerModalV4';
import CommonDialog from '../../../common/CommonDialog';
import LinkedDocumentLink, {
    getEstimateDetailUrl,
    getInvoiceSyncFromWorkOrderUrl,
    SYNC_PROMPT_TEXT,
    workOrderInvoiceToSync,
} from '../../../common/LinkedDocumentLink';
import { APPOINTMENT_STATUS, USER_TYPES } from '../../../config/dataService/constants';
import { appointmentKeys, useAppointmentDetails } from '../../../services/business/appointment';
import { useDeleteWorkOrder, useMarkWorkOrderAuthorized, useSendWorkOrderEmail, waitForWorkOrderStatus } from '../../../services/business/workOrders';
import { normalizeWorkOrderStatus, WORK_ORDER_STATUS, WORK_ORDER_STATUS_LABEL } from '../../../utility/Constant';
import { getItem } from '../../../utility/localStorage';
import ResendSvg from '../../../utility/Svg/ResendSvg';
import SaveAndSendSvg from '../../../utility/Svg/SaveAndSendSvg';
import SyncUpdateIcon from '../../../utility/Svg/SyncUpdateIcon';
import AppointmentModel from '../../Appointment/AppointmentModel';
import CancelWorkOrderModal from '../WorkOrderCommonComponents/CancelWorkOrderModal';
import WorkOrderDocument from '../WorkOrderCommonComponents/WorkOrderDocument';
import WorkOrderHistoryModal from '../WorkOrderCommonComponents/WorkOrderHistoryModal';

const HEADER_BUTTON_STYLE = {
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.04px',
    fontSize: '14px',
    height: '42px',
    // Keep each label on one line — a narrow screen wraps the row, not the words inside a button.
    whiteSpace: 'nowrap',
};

/**
 * Work Order detail — Open / Authorized / Invoiced / Cancelled.
 *
 * Header layout, breadcrumb and button styling mirror the invoice detail page. The one
 * addition is MARK AS AUTHORIZED, green while the work order can still be authorized and
 * muted once it has been (spec §4.4).
 */
const WorkOrderView = ({ workOrderId, workOrderData, fetchApi }) => {
    const navigate = useNavigate();
    const shopName = getItem('apex-saas_details')?.shopName;

    const [anchorEl, setAnchorEl] = useState(null);
    const [openHistoryModal, setOpenHistoryModal] = useState(false);
    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [isDeleteModal, setIsDeleteModal] = useState(false);

    const queryClient = useQueryClient();

    // "View Appointment" opens the shared appointment modal, exactly as the invoice detail page does.
    const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
    const [appointmentIdForModal, setAppointmentIdForModal] = useState(null);

    const { data: appointmentDetailData, isLoading: isAppointmentDetailLoading } = useAppointmentDetails(appointmentIdForModal, {
        enabled: !!appointmentIdForModal,
    });

    const appointmentModalColumns = useMemo(() => {
        const detail = appointmentDetailData?.data;
        if (!detail?.status) return {};
        return { [detail.status]: { name: detail.status, items: [detail] } };
    }, [appointmentDetailData?.data]);

    const refreshAppointmentDetails = () => {
        if (appointmentIdForModal) {
            queryClient.invalidateQueries({ queryKey: appointmentKeys.details(appointmentIdForModal) });
        }
    };

    const handleCloseAppointmentModal = () => {
        setAppointmentModalOpen(false);
        setAppointmentIdForModal(null);
        handleCloseMenu();
        fetchApi?.();
    };

    const markAuthorized = useMarkWorkOrderAuthorized();
    const deleteWorkOrder = useDeleteWorkOrder();

    const openMoreActions = Boolean(anchorEl);
    const handleCloseMenu = () => setAnchorEl(null);

    // Set when this work order was converted from an estimate — the API returns the estimate record.
    const sourceEstimate = Array.isArray(workOrderData?.linkedEstimateId) ? workOrderData?.linkedEstimateId?.[0] : workOrderData?.linkedEstimateId;
    const sourceEstimateNumber = sourceEstimate?.estimateNumber;

    /** The invoice this work order was converted into, if any. */
    const linkedInvoice = Array.isArray(workOrderData?.linkedInvoiceId) ? workOrderData?.linkedInvoiceId?.[0] : workOrderData?.linkedInvoiceId;

    /** Invoice detail url by status — the same mapping the estimate detail page uses. */
    const getLinkedInvoiceUrl = () => {
        if (!linkedInvoice?._id) return '#';

        let urlStatus;
        if (linkedInvoice?.status === 'draft') {
            urlStatus = 'draft';
        } else if (linkedInvoice?.status === 'sent') {
            urlStatus = linkedInvoice?.isEmailSent ? 'sent' : 'finalized';
        } else {
            urlStatus = linkedInvoice?.status || 'finalized';
        }
        return `/invoice/${urlStatus}/${linkedInvoice._id}`;
    };

    /** Estimate detail url by status — shared with every other linked estimate. */
    const getLinkedEstimateUrl = () => getEstimateDetailUrl(sourceEstimate);

    /** Work order ⇒ Invoice sync (spec §6.5): the linked invoice when it has fallen behind this work order. */
    const invoiceToSync = workOrderInvoiceToSync(workOrderData);
    const handleUpdateToInvoice = () => {
        if (!invoiceToSync?._id) return;
        navigate(getInvoiceSyncFromWorkOrderUrl(invoiceToSync._id), { state: { workOrderData } });
    };

    const status = normalizeWorkOrderStatus(workOrderData?.status);
    const isDraft = status === WORK_ORDER_STATUS.DRAFT;
    const isAuthorized = status === WORK_ORDER_STATUS.AUTHORIZED;
    const isCancelled = status === WORK_ORDER_STATUS.CANCELLED;
    const isInvoiced = status === WORK_ORDER_STATUS.INVOICED;
    const isEmailSent = Boolean(workOrderData?.isEmailSent);

    const [openEditCustomer, setOpenEditCustomer] = useState(false);
    const sendWorkOrderEmail = useSendWorkOrderEmail();
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    const hasCustomerEmail = Boolean(workOrderData?.customer?.email);

    const handleSend = async () => {
        setIsSendingEmail(true);
        try {
            await sendWorkOrderEmail.mutateAsync(workOrderId);
            toast.success('Work order was successfully emailed to customer');
            await fetchApi?.();
        } catch (error) {
            console.error('Error sending work order email:', error);
        } finally {
            setIsSendingEmail(false);
        }
    };

    /**
     * Authorization button (spec §4.4):
     *   open / reapproval → green and clickable
     *   authorized        → shown, muted and disabled
     *   draft / invoiced / cancelled → hidden (a draft cannot be authorized)
     */
    const canAuthorize = status === WORK_ORDER_STATUS.OPEN || status === WORK_ORDER_STATUS.REAPPROVAL;

    /**
     * Status gates exactly one thing (spec §9 R5): converting to an invoice. Every other action —
     * download, edit, cancel, history, delete — behaves identically in every status.
     */
    const canConvertToInvoice = isAuthorized;
    // Open / Reapprove are authorized on the way into the conversion (client: save the extra click).
    const authorizesOnConvert = canAuthorize;

    /**
     * Marking authorized is a plain status change through PUT change-status/:id { status:
     * 'authorised' } — it moves no inventory (spec §9 S7/S8), so there is no stock check here.
     *
     * The backend owns the status, so route by what it returns and fall back to `authorized`
     * only if the response carries none.
     */
    /**
     * True from the click until the page has refetched and moved to the new status route. A
     * full-page loader covers the page for that whole span, so nothing — View PDF in particular —
     * can be clicked against the stale record and its old PDF url.
     */
    const [isAuthorizing, setIsAuthorizing] = useState(false);

    const handleMarkAuthorized = async () => {
        setIsAuthorizing(true);
        try {
            const result = await markAuthorized.mutateAsync(workOrderId);

            await fetchApi?.();
            /*
             * The backend decides the status: authorizing a Reapprove work order that already has an
             * invoice lands on Invoiced, not Authorized. Read it back rather than assuming, so the
             * redirect goes to the page that will actually render.
             */
            const nextStatus = (await waitForWorkOrderStatus(workOrderId, '')) || normalizeWorkOrderStatus(result?.status) || WORK_ORDER_STATUS.AUTHORIZED;

            // One message for every status change — the backend decides the status and the page moves to it.
            toast.success('Work order status updated successfully');

            if (nextStatus !== status) {
                navigate(`/work-orders/${nextStatus}/${workOrderId}`, { replace: true });
            }
        } catch (error) {
            console.error('Error authorizing work order:', error);
        } finally {
            setIsAuthorizing(false);
        }
    };

    const handleViewPdf = () => {
        if (!workOrderData?.pdfUrl) {
            toast.error('PDF is not available for this work order.');
            return;
        }
        window.open(workOrderData.pdfUrl, '_blank', 'noopener,noreferrer');
    };

    /**
     * Convert to invoice (spec §4.4, §6.2): Open / Reapprove are authorized first, Authorized goes
     * straight through. No "invoice already exists" warning any more — a work order whose estimate has
     * an invoice is auto-linked to it (spec §6.4 flow 6), so it shows View Invoice instead.
     */
    const goToInvoiceConversion = async () => {
        if (authorizesOnConvert) {
            // Authorize first, with the full-page loader up, then open the conversion.
            setIsAuthorizing(true);
            try {
                await markAuthorized.mutateAsync(workOrderId);
                toast.success('Work order status updated successfully');
            } catch (error) {
                console.error('Error authorizing work order before conversion:', error);
                setIsAuthorizing(false);
                return;
            }
            setIsAuthorizing(false);
        }
        navigate('/invoice/new-invoice-from-work-order', { state: { workOrderData } });
    };

    const handleConvertToInvoice = () => {
        handleCloseMenu();
        if (!canConvertToInvoice && !authorizesOnConvert) {
            toast.error('Only authorized work orders can be converted to an invoice.');
            return;
        }

        goToInvoiceConversion();
    };

    const handleDelete = async () => {
        try {
            await deleteWorkOrder.mutateAsync(workOrderId);
            toast.success('Work order deleted successfully');
            navigate('/work-orders/all');
        } catch (error) {
            console.error('Error deleting work order:', error);
        } finally {
            setIsDeleteModal(false);
        }
    };

    return (
        <Box padding={'32px 24px'} sx={{ bgcolor: 'background.main' }}>
            {/* position: relative — the links below are pinned to this header's top-right corner, so one
                link and two take exactly the same room and nothing shifts down. */}
            <Grid container sx={{ padding: '16px 0px', position: 'relative' }} gap={'8px'}>
                <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'flex-start', md: 'center' },
                        justifyContent: 'space-between',
                        gap: '8px',
                    }}
                >
                    <div role="presentation">
                        <Breadcrumbs aria-label="breadcrumb">
                            <Link to="/work-orders/all" style={{ color: 'black', opacity: '0.6', textDecoration: 'none' }}>
                                Work Orders
                            </Link>
                            <Link to={`/work-orders/${status}`} style={{ color: 'black', opacity: '0.6', textDecoration: 'none' }}>
                                {WORK_ORDER_STATUS_LABEL[status]}
                            </Link>
                            <Link to={`/work-orders/${status}/${workOrderId}`} style={{ color: 'black', textDecoration: 'none' }}>
                                {workOrderData?.workOrderNumber}
                            </Link>
                        </Breadcrumbs>
                    </div>

                    {/* Linked documents: the invoice first, the source estimate beneath it. */}
                    <Stack
                        alignItems="flex-end"
                        spacing={0.5}
                        // Pinned to the header's corner on a normal screen; in the flow on a phone, where
                        // there is no room beside the breadcrumb.
                        sx={{ position: { xs: 'static', md: 'absolute' }, top: { md: '16px' }, right: 0 }}
                    >
                        {linkedInvoice?._id && (
                            <LinkedDocumentLink
                                to={getLinkedInvoiceUrl()}
                                documentNumber={linkedInvoice?.invoiceNumber}
                                // Hover shows "Update To Invoice" only while the invoice is behind this work order (spec §6.5).
                                syncPrompt={
                                    invoiceToSync
                                        ? { message: SYNC_PROMPT_TEXT.WORK_ORDER_TO_INVOICE, actionLabel: 'UPDATE TO INVOICE', onAction: handleUpdateToInvoice }
                                        : undefined
                                }
                            />
                        )}

                        {sourceEstimateNumber && <LinkedDocumentLink to={getLinkedEstimateUrl()} documentNumber={sourceEstimateNumber} />}
                    </Stack>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Typography variant="h4" sx={{ fontWeight: '500' }}>
                        {workOrderData?.workOrderNumber}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2} justifyContent="end">
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    {/* Wraps onto more rows when narrow — left aligned there, right aligned once there is room. */}
                    <Stack
                        direction={'row'}
                        spacing={2}
                        useFlexGap
                        sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' }, rowGap: '12px' }}
                    >
                        <Button
                            variant="contained"
                            color="secondary"
                            className="btn-text-size-manage"
                            startIcon={<VisibilityIcon />}
                            onClick={handleViewPdf}
                            style={HEADER_BUTTON_STYLE}
                        >
                            View PDF
                        </Button>

                        {!isDraft && !isCancelled && (
                            <Button
                                variant="contained"
                                startIcon={<SaveAndSendSvg disabled={isEmailSent} />}
                                disabled={isEmailSent || isSendingEmail}
                                onClick={() => {
                                    if (hasCustomerEmail) {
                                        handleSend();
                                    } else {
                                        setOpenEditCustomer(true);
                                    }
                                }}
                                style={HEADER_BUTTON_STYLE}
                            >
                                Email to Customer
                            </Button>
                        )}

                        {/* Only Open and Reapprove can be authorized; every other status hides the button. */}
                        {canAuthorize && (
                            <Button
                                variant="contained"
                                className="btn-text-size-manage"
                                disabled={isAuthorizing}
                                // No confirmation step — the client wants one click to authorize.
                                onClick={handleMarkAuthorized}
                                style={HEADER_BUTTON_STYLE}
                                sx={{
                                    background: '#40D158',
                                    color: '#FFF',
                                    '&:hover': { background: '#37B94C' },
                                    '&.Mui-disabled': { background: 'rgba(64, 209, 88, 0.40)', color: 'rgba(0, 0, 0, 0.40)' },
                                }}
                            >
                                Mark as Authorized
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="greyColor"
                            id="work-order-actions-button"
                            aria-controls={openMoreActions ? 'work-order-actions-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={openMoreActions ? 'true' : undefined}
                            onClick={(event) => setAnchorEl(event.currentTarget)}
                            size="large"
                            style={{
                                ...HEADER_BUTTON_STYLE,
                                width: '182px',
                                letterSpacing: '0.4px',
                                color: '#000000',
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.25)',
                            }}
                        >
                            more actions
                            <ExpandMoreIcon style={{ marginLeft: '8px' }} />
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            <WorkOrderDocument workOrderData={workOrderData} shopName={shopName} />

            <Menu
                style={{ top: 3 }}
                id="work-order-actions-menu"
                aria-labelledby="work-order-actions-button"
                anchorEl={anchorEl}
                open={openMoreActions}
                onClose={handleCloseMenu}
                // Same width as the MORE ACTIONS button and sitting directly under it, like the estimate and
                // invoice menus (they hard-code the button's width; here it is measured, so it always matches).
                sx={{
                    '& .MuiPaper-root': { width: '182px', minWidth: 'unset!important' },
                    '& .MuiMenuItem-root': { padding: '6px 12px!important', width: '100%' },
                }}
            >
                <MenuItem
                    onClick={() => {
                        navigate(`/work-orders/edit/${workOrderId}`);
                        handleCloseMenu();
                    }}
                >
                    <Edit style={{ marginRight: '8px', fontSize: '20px' }} /> Edit Work Order
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        navigate(`/customers/${workOrderData?.customer?._id}/${workOrderData?.customer?.createdFrom}`);
                        handleCloseMenu();
                    }}
                >
                    <VisibilityIcon style={{ marginRight: '8px', fontSize: '20px' }} />
                    View Customer
                </MenuItem>

                {/* Resend — once emailed; the first send is the EMAIL TO CUSTOMER button. */}
                {isEmailSent && !isDraft && !isCancelled && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            if (hasCustomerEmail) {
                                handleSend();
                            } else {
                                setOpenEditCustomer(true);
                            }
                        }}
                    >
                        <div style={{ marginRight: '8px', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ResendSvg />
                        </div>
                        Resend
                    </MenuItem>
                )}

                {workOrderData?.appointment?._id && (
                    <MenuItem
                        onClick={() => {
                            const linkedAppointmentId = workOrderData?.appointment?._id;
                            const appointmentStatus = workOrderData?.appointment?.status;

                            if (!linkedAppointmentId) return;

                            handleCloseMenu();

                            // A paid appointment has its own read-only page; everything else opens the modal.
                            if (appointmentStatus === APPOINTMENT_STATUS.CUSTOMER_PAID) {
                                navigate(`/appointment/past-appointment/${linkedAppointmentId}`);
                            } else {
                                queryClient.invalidateQueries({ queryKey: appointmentKeys.details(linkedAppointmentId) });
                                setAppointmentIdForModal(linkedAppointmentId);
                                setAppointmentModalOpen(true);
                            }
                        }}
                    >
                        <div style={{ width: '20px', height: '20px', marginRight: '8px' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                    d="M15.8333 2.50016H12.35C12 1.5335 11.0833 0.833496 10 0.833496C8.91667 0.833496 8 1.5335 7.65 2.50016H4.16667C3.25 2.50016 2.5 3.25016 2.5 4.16683V17.5002C2.5 18.4168 3.25 19.1668 4.16667 19.1668H15.8333C16.75 19.1668 17.5 18.4168 17.5 17.5002V4.16683C17.5 3.25016 16.75 2.50016 15.8333 2.50016ZM10 2.50016C10.4583 2.50016 10.8333 2.87516 10.8333 3.3335C10.8333 3.79183 10.4583 4.16683 10 4.16683C9.54167 4.16683 9.16667 3.79183 9.16667 3.3335C9.16667 2.87516 9.54167 2.50016 10 2.50016ZM15.8333 17.5002H4.16667V4.16683H5.83333V6.66683H14.1667V4.16683H15.8333V17.5002Z"
                                    fill="black"
                                />
                            </svg>
                        </div>
                        View Appointment
                    </MenuItem>
                )}

                {/*
                 * Linked invoice: Update To Invoice while it is behind this work order, View Invoice
                 * otherwise. No invoice: Convert To Invoice. A cancelled work order can only view one —
                 * with no invoice linked it offers nothing here.
                 */}
                {linkedInvoice?._id ? (
                    invoiceToSync && !isCancelled ? (
                        <MenuItem
                            onClick={() => {
                                handleCloseMenu();
                                handleUpdateToInvoice();
                            }}
                        >
                            <SyncUpdateIcon /> Update To Invoice
                        </MenuItem>
                    ) : (
                        <MenuItem
                            onClick={() => {
                                handleCloseMenu();
                                navigate(getLinkedInvoiceUrl());
                            }}
                        >
                            <ReceiptLong style={{ marginRight: '8px', fontSize: '20px' }} /> View Invoice
                        </MenuItem>
                    )
                ) : isCancelled ? null : (
                    <MenuItem onClick={handleConvertToInvoice}>
                        <ReceiptLong style={{ marginRight: '8px', fontSize: '20px' }} /> Convert To Invoice
                    </MenuItem>
                )}

                <MenuItem
                    onClick={() => {
                        setOpenHistoryModal(true);
                        handleCloseMenu();
                    }}
                >
                    <HistoryIcon style={{ marginRight: '8px', fontSize: '20px' }} /> History
                </MenuItem>

                {/* Cancel is offered in every status except Draft, Invoiced and an already-cancelled one. */}
                {!isCancelled && !isDraft && !isInvoiced && (
                    <MenuItem
                        onClick={() => {
                            setOpenCancelModal(true);
                            handleCloseMenu();
                        }}
                    >
                        <Close style={{ marginRight: '8px', fontSize: '20px' }} /> Cancel Work Order
                    </MenuItem>
                )}

                <MenuItem
                    onClick={() => {
                        setIsDeleteModal(true);
                        handleCloseMenu();
                    }}
                >
                    <Delete style={{ marginRight: '8px', color: '#D32F2F', fontSize: '20px' }} /> <span style={{ color: '#D32F2F' }}>Delete</span>
                </MenuItem>
            </Menu>

            <WorkOrderHistoryModal open={openHistoryModal} onClose={() => setOpenHistoryModal(false)} workOrderId={workOrderId} />

            <CancelWorkOrderModal open={openCancelModal} onClose={() => setOpenCancelModal(false)} workOrder={workOrderData} onCancelled={fetchApi} />

            <AppointmentModel
                open={appointmentModalOpen}
                handleClose={handleCloseAppointmentModal}
                data={appointmentDetailData?.data}
                status={appointmentDetailData?.data?.status}
                fetchAppointmentDetails={refreshAppointmentDetails}
                recallDetailsAPI={refreshAppointmentDetails}
                columns={appointmentModalColumns}
                setAppointmentDropOffODo={() => {}}
                setAppointmentCompletionOffODo={() => {}}
                setUpdateStatus={() => {}}
                setSelectId={() => {}}
                fetchCalendarDataonAppointmentStatusChange={() => {}}
                show={false}
                handleStatusClose={() => {}}
                handleStatus={() => {}}
                fromCaledner={false}
            />

            {/* Full-page loader while authorizing or sending email — blocks the page until the action and data refresh complete. */}
            <Dialog open={isAuthorizing || isSendingEmail}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>

            <Dialog open={appointmentModalOpen && isAppointmentDetailLoading}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>

            <CommonDialog
                open={isDeleteModal}
                onClose={() => setIsDeleteModal(false)}
                onCancel={() => setIsDeleteModal(false)}
                onSave={handleDelete}
                loader={deleteWorkOrder.isPending}
                dialogTitle="Delete work order?"
                dialogSubtitle="This work order will be permanently deleted. This action cannot be undone."
                cancelButtonText="CANCEL"
                saveButtonText="DELETE"
            />

            {openEditCustomer && (
                <Suspense fallback={<CircularProgress />}>
                    <AddCustomerModalV4
                        from="invoice_email"
                        openModal={openEditCustomer}
                        customerId={workOrderData?.customer?.refIdImport || workOrderData?.customer?.refIdUsers || workOrderData?.customer?._id}
                        customerType={workOrderData?.customer?.createdFrom || workOrderData?.customer?.source || USER_TYPES.SHOP_CREATED}
                        onClose={() => {
                            setOpenEditCustomer(false);
                        }}
                        onSubmitModal={async () => {
                            setOpenEditCustomer(false);
                            await handleSend();
                        }}
                    />
                </Suspense>
            )}
        </Box>
    );
};

export default WorkOrderView;
